import { openDB, type IDBPDatabase } from 'idb'
import { getModelMetadata } from 'src/CrudManager/CrudModels/generated-crud'
import type { PreviewItem } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { getSocket } from 'src/CrudManager/Core/Utils/echoSocket'

/**
 * Map a frontend model alias to the backend-config model name.
 * `invoice`, `quote`, etc. all share `transaction` on the backend; the registry
 * carries the mapping via `apiUrl`. The cache key stays on the frontend alias
 * so each visible variant keeps its own cached preview record.
 */
function resolveBackendModelName(modelName: string): string {
  const meta = getModelMetadata(modelName) as { apiUrl?: string } | undefined
  return meta?.apiUrl ?? modelName
}

// Batch loading queue
interface BatchQueueItem {
  modelName: string
  id: string
  resolve: (value: PreviewData | PromiseLike<PreviewData>) => void
  reject: (reason?: unknown) => void
}

// Add a helper type for grouping queue items
type GroupedQueueItems = Record<
  string,
  {
    modelName: string
    id: string
    resolvers: BatchQueueItem[]
  }
>

let batchQueue: BatchQueueItem[] = []
let batchTimeout: NodeJS.Timeout | null = null
const BATCH_DELAY = 50 // ms to wait before sending batch request

export interface PreviewData {
  id: string | null
  model: string
  loading: boolean
  error: boolean
  title: string
  data: PreviewItem | null
  updatedAt: Date
}

// Single object store for all previews - model-agnostic
const DB_NAME = 'Previews'
const STORE_NAME = 'previews'
const DB_VERSION = 1

// Composite key: `${model}:${id}`
type PreviewKey = string

// Event emitter for reactivity (replaces Dexie's liveQuery)
interface PreviewUpdateEvent {
  key: PreviewKey
  data: PreviewData | null
}

const updateListeners = new Set<(event: PreviewUpdateEvent) => void>()

function notifyListeners(key: PreviewKey, data: PreviewData | null) {
  const event: PreviewUpdateEvent = { key, data }
  updateListeners.forEach((listener) => {
    try {
      listener(event)
    } catch (error) {
      console.error('Error in preview update listener:', error)
    }
  })
}

// Subscribe to preview updates (replaces Dexie's liveQuery)
export function subscribeToPreview(modelName: string, id: string, callback: (data: PreviewData | null) => void): () => void {
  const key = getPreviewKey(modelName, id)

  const listener = (event: PreviewUpdateEvent) => {
    if (event.key === key) {
      callback(event.data)
    }
  }

  updateListeners.add(listener)

  // Immediately fetch current value
  getPreviewData(modelName, id)
    .then(callback)
    .catch(() => callback(null))

  // Return unsubscribe function
  return () => {
    updateListeners.delete(listener)
  }
}

function getPreviewKey(modelName: string, id: string): PreviewKey {
  return `${modelName}:${id}`
}

// Initialize database
let dbPromise: Promise<IDBPDatabase> | null = null

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create a single object store for all previews
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' })
          // Create indexes for efficient querying
          store.createIndex('model', 'model', { unique: false })
          store.createIndex('id', 'id', { unique: false })
          store.createIndex('updatedAt', 'updatedAt', { unique: false })
        }
      },
    })
  }
  return dbPromise
}

// Internal storage format (includes composite key)
interface StoredPreviewData extends PreviewData {
  key: PreviewKey
}

// Convert stored format to PreviewData
function fromStored(stored: StoredPreviewData | undefined): PreviewData | null {
  if (!stored) return null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { key, ...previewData } = stored
  return previewData
}

// Convert PreviewData to stored format
function toStored(modelName: string, id: string, data: PreviewData): StoredPreviewData {
  return {
    ...data,
    key: getPreviewKey(modelName, id),
    model: modelName,
    id: id,
  }
}

// Get preview data from cache
async function getPreviewData(modelName: string, id: string): Promise<PreviewData | null> {
  const db = await getDB()
  const key = getPreviewKey(modelName, id)
  const stored = await db.get(STORE_NAME, key)
  return fromStored(stored)
}

// Update preview data in cache
async function updatePreviewData(modelName: string, id: string, data: PreviewData): Promise<void> {
  const db = await getDB()
  const key = getPreviewKey(modelName, id)
  const stored = toStored(modelName, id, data)
  await db.put(STORE_NAME, stored)
  notifyListeners(key, data)
}

// Query previews by model (useful for debugging/cleanup)
export async function getPreviewsByModel(modelName: string): Promise<PreviewData[]> {
  const db = await getDB()
  const index = db.transaction(STORE_NAME, 'readonly').store.index('model')
  const stored = await index.getAll(modelName)
  return stored.map(fromStored).filter((d): d is PreviewData => d !== null)
}

// Fetch every cached preview at once. Keyed by the record id (not model:id) since
// the incoming lookup side (searching related records inside a recent's data)
// only has the foreign-key uuid, not the model it points to.
export async function getAllPreviews(): Promise<Map<string, { title: string; model: string }>> {
  const db = await getDB()
  const stored = (await db.getAll(STORE_NAME)) as StoredPreviewData[]
  const map = new Map<string, { title: string; model: string }>()
  for (const entry of stored) {
    if (entry.id && entry.title) {
      map.set(entry.id, { title: entry.title, model: entry.model })
    }
  }
  return map
}

// Add a new function for direct fetching (no batching)
const fetchPreviewDataDirectly = async (modelName: string, id: string): Promise<PreviewData> => {
  const formParams = new URLSearchParams({ model: resolveBackendModelName(modelName) })

  const { data } = await axiosApi.get(`cm/preview/${id}`, {
    params: formParams,
  })

  const previewData: PreviewData = {
    id: data.id,
    model: modelName,
    error: false,
    title: data.title,
    data: data.data,
    loading: false,
    updatedAt: new Date(),
  }

  // Update cache
  await updatePreviewData(modelName, id, previewData)

  return previewData
}

let hasBeenInitiated = false

export const initSocket = () => {
  if (hasBeenInitiated) return
  const socket = getSocket()
  if (socket) {
    socket.private('CrudModels').listen('.App\\Packages\\CrudManager\\Events\\CrudModelUpdated', async (data) => {
      if (!data.modelName || !data.id) {
        console.error('Missing required data:', { modelName: data.modelName, id: data.id })
        return
      }

      try {
        // Use direct fetch for socket updates to ensure immediate refresh
        await fetchPreviewDataDirectly(data.modelName, data.id)
      } catch (error) {
        console.error('Error in socket event handler:', error)
      }
    })
    hasBeenInitiated = true
  } else {
    setTimeout(() => {
      initSocket()
    }, 10000)
  }
}

// Helper to check if preview data is stale (older than 5 minutes)
export const isPreviewStale = (updatedAt: Date): boolean => {
  const staleThreshold = 5 * 60 * 1000 // 5 minutes in milliseconds
  const now = Date.now()
  const itemTime = new Date(updatedAt).getTime()
  return now - itemTime > staleThreshold
}

// Add this new function to handle batch loading
const processBatchQueue = async () => {
  if (batchQueue.length === 0) return

  const currentBatch = [...batchQueue]
  batchQueue = []
  batchTimeout = null

  // Group queue items by model+id to deduplicate requests
  const groupedItems: GroupedQueueItems = {}
  for (const item of currentBatch) {
    const key = `${item.modelName}:${item.id}`
    if (!groupedItems[key]) {
      groupedItems[key] = {
        modelName: item.modelName,
        id: item.id,
        resolvers: [],
      }
    }
    groupedItems[key].resolvers.push(item)
  }

  try {
    // Map each frontend alias to its backend model name so the API receives a name
    // it can resolve via crudmodels config. Track the reverse map to find the
    // originating frontend alias when results come back.
    const aliasByBackend: Record<string, string[]> = {}
    const items = Object.values(groupedItems).map((group) => {
      const backendName = resolveBackendModelName(group.modelName)
      const list = aliasByBackend[backendName] ?? []
      if (!list.includes(group.modelName)) list.push(group.modelName)
      aliasByBackend[backendName] = list
      return { model: backendName, id: group.id }
    })

    const { data } = await axiosApi.post('cm/batch-previews', { items })

    // Process results and update cache. Replay each result onto every frontend
    // alias that requested it (e.g. invoice + quote both fanned to transaction).
    for (const preview of data) {
      const backendName = preview.model || preview.data?.model
      const previewId = preview.id
      const aliases = aliasByBackend[backendName] ?? [backendName]

      for (const alias of aliases) {
        const previewData: PreviewData = {
          id: previewId,
          model: alias,
          error: false,
          title: preview.title,
          data: preview.data,
          loading: false,
          updatedAt: new Date(),
        }

        await updatePreviewData(alias, previewId, previewData)

        const previewKey = `${alias}:${previewId}`
        const group = groupedItems[previewKey]
        if (group) {
          group.resolvers.forEach((item) => item.resolve(previewData))
        }
      }
    }

    // Reject any items that weren't found in the response
    for (const group of Object.values(groupedItems)) {
      const backendName = resolveBackendModelName(group.modelName)
      const found = data.some((preview) => preview.id === group.id && (preview.model === backendName || preview.data?.model === backendName))
      if (!found) {
        group.resolvers.forEach((item) => item.reject(new Error('Preview not found')))
      }
    }
  } catch (error) {
    // If the batch request fails, reject all promises
    for (const group of Object.values(groupedItems)) {
      group.resolvers.forEach((item) => item.reject(error))
    }
  }
}

export const fetchSinglePreviewData = async (modelName: string, id: string): Promise<PreviewData> => {
  // First check cache
  const cachedItem = await getPreviewData(modelName, id)
  if (cachedItem && cachedItem.data && !isPreviewStale(cachedItem.updatedAt)) {
    return cachedItem
  }

  // If not in cache or stale, add to batch queue
  return new Promise((resolve, reject) => {
    batchQueue.push({ modelName, id, resolve, reject })

    if (batchTimeout) {
      clearTimeout(batchTimeout)
    }

    batchTimeout = setTimeout(processBatchQueue, BATCH_DELAY)
  })
}
